import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route  } from "react-router-dom";
import { useLocation, useNavigationType } from "react-router-dom";
import axios from 'axios';
import Constants from './Utilities/Constants';
import { AppProvider, useAppContext } from './Contexts/AppContext';
import LoadingScreen from './Components/LoadingScreen';
import DatabaseCheck from './Components/DatabaseCheck';
import HomePage from "./Pages/HomePage"
import Header from "./Components/Header";
import SmartHeader from "./Components/SmartHeader";
import Footer from "./Components/Footer";
import SideCart from "./Components/SideCart";
import Category from "./Pages/Category";
import SignIn from "./Pages/Login";
import ResetPassword from "./Pages/ResetPassword";
import SignUp from "./Pages/Signup";
import Profile from "./Pages/Profile";
import Cart from "./Pages/Cart";
import Checkout from "./Pages/Checkout";
import ProductPage from "./Pages/ProductPage";
import ProductModal from "./Pages/ProductModal";
import Ingredients from "./Components/Indegrients";
import SearchProducts from "./Pages/SearchProducts";
import Page from "./Pages/Page";
import ThankYou from "./Pages/ThankYou";
import SmartFooter from "./Components/SmartFooter";
import ValueCard from "./Pages/ValueCard";
import ErrorBoundary from './Components/ErrorBoundary';
import NotFound from './Pages/NotFound';
import ChatButton from './Components/Chat/ChatButton';
import AiResults from './Pages/AiResults';
import LoginModal from './Components/LoginModal';

const ResponsiveComponent = ({ isLargeScreen }) => {
  return (
    <>
      {isLargeScreen 
      ? <>
        <Header />
        <Footer />
       </> 
       : <>
        <SmartHeader />
        <SmartFooter />
        </>
       }
      </>
  );
};


const App = () => {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
      const initializeApp = async () => {
          try {
              // Load configuration first
              await Constants.initialize();
              axios.defaults.headers.common['X-Api-Key'] = Constants.API_KEY;
              setConfigLoaded(true);
          } catch (error) {
              console.error('Failed to initialize configuration:', error);
              setConfigError(error.message);
              // Still set loaded to true to use fallback config
              setConfigLoaded(true);
          }
      };

      initializeApp();
  }, []);

  // Show loading while config is loading
  if (!configLoaded) {
      return <LoadingScreen message="טוען הגדרות..." />;
  }

  // Show maintenance mode if enabled
  if (Constants.isFeatureEnabled('maintenanceMode')) {
      return (
          <div className="container text-center p-5">
              <h1>🔧 אתר בתחזוקה</h1>
              <p>האתר נמצא כעת בתחזוקה. אנא חזרו מאוחר יותר.</p>
          </div>
      );
  }

  return (
    <>
      <Router>
        <DatabaseCheck>
            <AppProvider>
                {configError && (
                  <div className="alert alert-warning">
                    שגיאה בטעינת הגדרות: {configError}
                  </div>
                )}
                <Layout />
            </AppProvider>
        </DatabaseCheck>
      </Router>
    </>
  )
}


const Layout = () => {
  const [isLargeScreen, setIsLargeScreen] = useState(() => window.innerWidth > 768);
  const { isInitialized } = useAppContext();
  const location = useLocation();

  // When user navigates within the app to open a product as a modal we store
  // the previous location in location.state.modal.backgroundLocation.
  // However a page reload (or browser back/forward) results in a POP
  // navigation — in that case we should NOT treat the route as a modal
  // and should render the full `ProductPage` instead. Use
  // `useNavigationType()` to detect POP and ignore modal state then.
  const navigationType = useNavigationType();

  const backgroundLocation =
    (navigationType === 'POP') ? null : location.state?.modal?.backgroundLocation;

  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isInitialized) {
    return <LoadingScreen message="טוען נתונים..." />; // Remove DataInitializer
  }


return (
  <>

    <ResponsiveComponent isLargeScreen={isLargeScreen} />
    <SideCart />
    <LoginModal />
    {Constants.isFeatureEnabled('enableChat') && <ChatButton />}

  <ErrorBoundary>
  <Routes location={backgroundLocation || location}>
      <Route path="/" element={<HomePage />} />
      <Route path="/Category/:category" element={<Category />} />
      <Route path="/Category/:category/:subcategory" element={<Category />} />
      <Route path="/Login" element={<SignIn />} />
      <Route path="/ResetPassword" element={<ResetPassword />} />
      <Route path="/Signup" element={<SignUp />} />
      <Route path="/Profile" element={<Profile />} />
      <Route path="/Cart" element={<Cart />} />
      <Route path="/Checkout" element={<Checkout />} />

      {/* FULL page product */}
      <Route path="/product/:productUrl" element={<ProductPage />} />

      <Route path="/Ingredients" element={<Ingredients />} />
      <Route path="/Search/:query" element={<SearchProducts />} />
      <Route path="/AiResults" element={<AiResults />} />
      <Route path="/Page/:url" element={<Page />} />
      <Route path="/ThankYou/:orderid" element={<ThankYou />} />
      <Route path="/ValueCard" element={<ValueCard />} />

      {/* Not found / fallback page */}
      <Route path="*" element={<NotFound />} />
    </Routes>

    {backgroundLocation && (
      <Routes>
        {/* MODAL only */}
        <Route path="/product/:productUrl" element={<ProductModal />} />
      </Routes>
    )}
    </ErrorBoundary>
    </>
  )
}

export default App;